import React, { useCallback, useEffect, useId, useState } from 'react';
import { ChevronDown, CircleHelp, Download, FileText, Info } from 'lucide-react';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import { formatDate } from 'v3/utils/controlTowerDate';
import {
  formatOccurrenceMoney,
  occurrenceHint,
  sumOccurrenceMoney,
  validSummary,
} from './contract';
import useOccurrences from './useOccurrences';
import useOccurrenceAttachments from './useOccurrenceAttachments';
import './styles.css';

const missing = 'Não informado';

/** Abas da 5660: as etiquetas somam as ocorrências da aba escolhida (resposta do negócio, 26/10). */
const TABS = [
  { key: 'ALL', label: 'Total de ocorrências', count: 'totalCount', match: () => true },
  { key: 'PENDING', label: 'Pendentes', count: 'pendingCount', match: (row) => row.pending },
  { key: 'COMPLETED', label: 'Concluídas', count: 'completedCount', match: (row) => !row.pending },
];
const TOTALS = [
  { key: 'payCarrier', label: 'Pagar transportador', className: 'tc-occ-tag-pay' },
  { key: 'receiverClient', label: 'Receber cliente', className: 'tc-occ-tag-receive' },
  { key: 'retainedBranch', label: 'Retido filial', className: 'tc-occ-tag-receive' },
];
const COLUMNS = [
  'Status',
  'Nº Ocorrência',
  'Tipo Ocorrência',
  'Grupo Responsável',
  'Início',
  'Término',
  'Nº Ocorrência Cliente',
  'Pagar Transportador',
  'Receber Cliente',
  'Retido Filial',
  'Inclusão',
];

function Attachments({ occurrenceId, count }) {
  const { loading, data, error } = useOccurrenceAttachments(occurrenceId, count > 0);
  return (
    <section className="tc-occ-box" aria-label="Anexos da ocorrência">
      <h4>
        <Download size={14} aria-hidden="true" /> Anexos ({count})
      </h4>
      {count === 0 && <p>Sem anexos.</p>}
      {loading && <p role="status">Carregando anexos…</p>}
      {error && <p role="alert">{error}</p>}
      {data && (
        <ul className="tc-occ-attachments">
          {data.map((file) => (
            <li key={file.id}>
              <a href={file.url} target="_blank" rel="noopener noreferrer" download>
                <Download size={14} aria-hidden="true" /> {file.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Occurrence({ row }) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const created = [row.createdBy, row.createdAt && formatDate(row.createdAt)]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      <tr className={open ? 'tc-occ-open' : undefined} data-occurrence-id={row.id}>
        <td>
          <button
            type="button"
            className="tc-occ-toggle"
            aria-expanded={open}
            aria-controls={detailsId}
            aria-label={`${open ? 'Fechar' : 'Abrir'} detalhes da ocorrência ${row.id}`}
            onClick={() => setOpen(!open)}
          >
            <ChevronDown size={16} aria-hidden="true" className="tc-occ-chevron" />
          </button>
        </td>
        <td>
          <span
            className={`tc-occ-badge ${row.pending ? 'tc-occ-badge-pending' : 'tc-occ-badge-done'}`}
          >
            {row.pending ? 'Pendente' : 'Concluída'}
          </span>
        </td>
        <td>{row.id}</td>
        <td>{row.type || missing}</td>
        <td>{row.groups.length ? row.groups.join(', ') : missing}</td>
        <td>{formatDate(row.startedAt)}</td>
        <td>{formatDate(row.endedAt)}</td>
        <td>{row.customerNumber || missing}</td>
        <td className={row.payCarrier !== null ? 'tc-occ-pay' : undefined}>
          {formatOccurrenceMoney(row.payCarrier)}
        </td>
        <td>{formatOccurrenceMoney(row.receiverClient)}</td>
        <td className={row.retainedBranch !== null ? 'tc-occ-retained' : undefined}>
          {formatOccurrenceMoney(row.retainedBranch)}
        </td>
        <td>{created || missing}</td>
      </tr>
      {open && (
        <tr id={detailsId} className="tc-occ-details">
          <td colSpan={COLUMNS.length + 1}>
            <div className="tc-occ-boxes">
              <section className="tc-occ-box" aria-label="Descrição da ocorrência">
                <h4>
                  <FileText size={14} aria-hidden="true" /> Descrição
                </h4>
                <p className="tc-occ-description">{row.description || 'Descrição não informada'}</p>
                {row.concludedBy && <small>Concluída por: {row.concludedBy}</small>}
              </section>
              <Attachments occurrenceId={row.id} count={row.attachmentCount} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function OccurrencesModal({ companyId, travelId, travelNumber, onClose, onSummary }) {
  const titleId = useId();
  const [revision, setRevision] = useState(0);
  const [tab, setTab] = useState('ALL');
  const { loading, data, error } = useOccurrences(companyId, travelId, revision);
  useEffect(() => {
    if (data) onSummary(data.summary);
  }, [data, onSummary]);
  const refresh = () => setRevision((value) => value + 1);
  const selected = TABS.find((item) => item.key === tab);
  const rows = data ? data.data.filter(selected.match) : [];
  return (
    <Modal
      show
      centered
      handleClose={onClose}
      size="xl"
      scrollable
      className="tc-occ-modal"
      aria-labelledby={titleId}
      heading={
        <span id={titleId}>
          Ocorrências da viagem: <span className="tc-occ-travel">{travelNumber || travelId}</span>
        </span>
      }
      body={
        <div className="tc-occ-body" aria-busy={loading}>
          {loading && <p role="status">Carregando ocorrências…</p>}
          {error && (
            <div role="alert" className="tc-occ-error">
              <p>{error}</p>
              <Button size="sm" onClick={refresh}>
                Tentar novamente
              </Button>
            </div>
          )}
          {data && (
            <>
              <ul className="tc-occ-tags" aria-label={`Totais: ${selected.label}`}>
                {TOTALS.map((total) => (
                  <li key={total.key} className={total.className}>
                    {total.label}{' '}
                    {formatOccurrenceMoney(sumOccurrenceMoney(rows.map((row) => row[total.key])))}
                  </li>
                ))}
              </ul>
              <div className="tc-occ-tabs" role="tablist" aria-label="Situação das ocorrências">
                {TABS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={item.key === tab}
                    onClick={() => setTab(item.key)}
                  >
                    {item.label} <span className="tc-occ-count">{data.summary[item.count]}</span>
                  </button>
                ))}
              </div>
              {rows.length === 0 ? (
                <p className="tc-occ-empty">
                  {data.summary.totalCount
                    ? `Nenhuma ocorrência ${tab === 'PENDING' ? 'pendente' : 'concluída'}.`
                    : 'Sem ocorrências nesta viagem.'}
                </p>
              ) : (
                <div className="tc-occ-table-scroll">
                  <table className="tc-occ-table">
                    <thead>
                      <tr>
                        <th scope="col">
                          <span className="visually-hidden">Detalhes</span>
                        </th>
                        {COLUMNS.map((column) => (
                          <th key={column} scope="col">
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <Occurrence key={row.id} row={row} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      }
    />
  );
}

/** Reutilizável em qualquer lista que forneça identidade da viagem e resumo do servidor. */
export default function Occurrences({ item, context }) {
  const scope = `${context.companyId}:${item.travelId}`;
  const [opened, setOpened] = useState(null);
  const [observed, setObserved] = useState(null);
  const source = item.occurrenceSummary;
  const onSummary = useCallback(
    (summary) => setObserved({ scope, source, summary }),
    [scope, source],
  );
  const summary =
    observed?.scope === scope && observed.source === source ? observed.summary : source;
  const known = validSummary(summary);
  const hint = occurrenceHint(summary);
  const Icon = known ? Info : CircleHelp;
  return (
    <>
      <button
        type="button"
        className={`tc-trip-icon tc-occ-indicator ${known && summary.state === 'PENDING' ? 'tc-occ-pending' : 'tc-occ-completed'}`}
        title={hint}
        aria-label={`${hint} — viagem ${item.travelNumber || item.travelId}`}
        aria-haspopup="dialog"
        onClick={() => setOpened(scope)}
      >
        <Icon size={16} aria-hidden="true" />
      </button>
      {opened === scope && (
        <OccurrencesModal
          key={scope}
          companyId={context.companyId}
          travelId={item.travelId}
          travelNumber={item.travelNumber}
          onClose={() => setOpened(null)}
          onSummary={onSummary}
        />
      )}
    </>
  );
}
